import { h, Component } from 'preact';
import { icons, getIcon } from 'utils/icons';
import { toast } from 'utils/modal';

import Button from 'components/button/button';
import SaveButton from 'components/button/saveButton';
import Dropdown from 'components/dropdown/dropdown';
import InlineSelect from 'components/inlineSelect/inlineSelect';
import Toggle from 'components/toggle/toggle';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';
import HelpTooltip from 'components/tooltip/helpTooltip';
import SimpleField from 'components/editor/settings/simpleField';
import DateField from 'components/editor/settings/dateField';
import LinkField from 'components/editor/settings/linkField';
import DropdownField from 'components/editor/settings/dropdownField';
import AutocompleteField from 'components/editor/settings/autocompleteField';
import CardSelectField from 'components/editor/settings/cardSelectField';
import RichTextField from 'components/editor/settings/richTextField';
import Badge from 'components/badge';
import Warning from 'components/warning/warning';

// Testing generic components

export default class ComponentsTest extends Component {
	render() {
		return (
			<div class="components-test">
				<style>{`
					.components-test {
						padding: 40px;
						fieldset { padding: 0; border: 1px solid #eee; border-radius: 8px; & + fieldset { margin-top: 32px; } }
						legend { position: relative; padding: 0 8px; margin-inline: 16px; height: 24px; line-height: 24px; z-index: 2; }
						legend code {
							color: #d35400;
							background: rgb(0 0 0 / 5%);
							border: 1px solid rgb(0 0 0 / 10%);
							border-radius: 3px;
							padding: 2px 3px 1px;
							font-size: 12px;
							line-height: 1.5;
							font-family: Monaco,Menlo,Consolas,Courier New,monospace;
						}
					}
				`}
				</style>
				<fieldset>
					<legend><code>Button</code> + <code>Dropdown</code> + <code>SaveButton</code></legend>
					<ButtonsSample />
				</fieldset>
				<fieldset>
					<legend><code>Toast</code></legend>
					<ToastTest />
				</fieldset>
				<fieldset>
					<legend><code>Sortable</code></legend>
					<SortTest />
				</fieldset>
				<fieldset>
					<legend><code>HelpTooltip</code></legend>
					<HelpTooltipTest />
				</fieldset>
				<fieldset>
					<legend>Fields (<code>SimpleField</code>, <code>RichTextField</code>...)</legend>
					<FieldsTest />
				</fieldset>
				<fieldset>
					<legend><code>Badge</code> + <code>Warning</code></legend>
					<MiscComponentsTest />
				</fieldset>
				<fieldset>
					{/* <legend><code>Icons</code></legend> */}
					<IconsTest />
				</fieldset>
			</div>
		);
	}
}

class ButtonsSample extends Component {
	constructor(props) {
		super(props);

		this.state = {
			custom: {
				content: 'Custom',
				outline: true,
				icon: 'none',
				iconAlt: 'none',
				iconSize: 'medium',
				counter: '9+',
			},
		};

		this.updateCustomButton = this.updateCustomButton.bind(this);
	}

	updateCustomButton(e) {
		const { name, value, type, checked } = e.target;
		const custom = {
			...this.state.custom,
			[name]: type === 'checkbox' ? checked : value
		};
		this.setState({ custom });
	}

	buttons(dark) {
		const custom = this.state.custom;
		const name = (id) => `btn-test-${id}-${dark ? 'dark' : 'light'}`;
		return (
			<div class={`buttons${dark ? ' dark' : ''}`}>
				<div class="name">Button (common)</div>
				<div class="category">
					<Button dark={dark}>Simple</Button>
					<Button outline dark={dark}>Outline</Button>
					<Button primary dark={dark}>Primary</Button>
					<Button danger dark={dark}>Danger</Button>
					<Button outline icon="check" dark={dark}>Icon</Button>
					<Button outline counter={10} dark={dark}>Counter</Button>
					<Button outline dark={dark}>
						Actions
						<div popover="auto" id={name('actions')}>
							<Button>
								Action 1
								{/* <div popover="auto" id={dropdownName('actions-nested')}>
									<Button>Action 1.1</Button>
								</div> */}
							</Button>
							<Button>Action 2</Button>
						</div>
					</Button>
				</div>
				<div class="name">Button (custom)</div>
				<div class="category">
					<Button
						text={custom.text}
						outline={custom.outline}
						primary={custom.primary}
						danger={custom.danger}
						small={custom.small}
						disabled={custom.disabled}
						loading={custom.loading}
						expandOnHover={custom.expandOnHover}
						icon={custom.icon}
						iconAlt={custom.iconAlt}
						iconSize={custom.iconSize}
						counter={custom.counterEnabled && custom.counter}
						popovertarget={custom.dropdown === 'self' && name('custom')}
						dark={dark}
					>
						{custom.content}
						{!!custom.dropdown && (
							<div popover="auto" id={name('custom')} align={custom.dropdown === 'self' ? 'left' : 'auto'}>
								<Button>Action 1</Button>
								<Button>Action 2</Button>
							</div>
						)}
					</Button>
				</div>
				<div class="name">Dropdown</div>
				<div class="category">
					<Dropdown name={name('dropdown')} dark={dark}>
						{/* <div class="dropdown-header">Dropdown</div>
						<hr /> */}
						<option value="1">Option 1</option>
						<option value="2">Option 2</option>
						<option value="3">Option 3</option>
					</Dropdown>
					<Dropdown name={name('dropdown-search')} searchable allowEmpty placeholder="Searchable" outline icon="eye" dark={dark}>
						<option value="1">First searchable item (1)</option>
						<option value="2">Second searchable item (2)</option>
						<option value="3">Third searchable item (3)</option>
					</Dropdown>
				</div>
				<div class="name">InlineSelect</div>
				<div class="category">
					<InlineSelect name={name('inline-select')} dark={dark}>
						<option value="1">Option 1</option>
						<option value="2">Option 2</option>
						<hr />
						<option value="3">Option 3</option>
					</InlineSelect>
				</div>
				<div class="name">InlineSelect (vertical)</div>
				<div class="category">
					<InlineSelect name={name('inline-select-vertical')} dark={dark} transparent vertical>
						<option value="1">Option 1</option>
						<option value="2">Option 2</option>
					</InlineSelect>
				</div>
				<div class="name">SaveButton</div>
				<div class="category">
					<SaveButton dummy dark={dark} />
				</div>
				<div class="name">Toggle</div>
				<div class="category">
					<Toggle dark={dark}>Switch</Toggle>
					<Toggle dark={dark} small>Small switch</Toggle>
				</div>
			</div>
		);
	}

	render() {
		const custom = this.state.custom;
		return (
			<div class="buttons-test">
				<style>{`
					.buttons-test {
						display: grid;
						grid-template-columns: 1fr 1fr;
						border-radius: inherit;
						overflow: hidden;
						.customization {
							grid-column: 1 / -1;
							border-bottom: 1px solid #eee;
							display: flex;
							flex-flow: column;
							align-items: start;
							gap: 8px;
							padding: 16px;
							accent-color: var(--primary);
							.group { display: flex; gap: 16px; }
							label { display: flex; align-items: center; gap: 4px; }
							input[type="text"], input[type="number"] { line-height: 1; }
							input[name="counter"] { width: 3em; }
							select, input { border: 1px solid #ccc; border-radius: 4px; background: #fff; }
						}
						.buttons {
							padding: 16px;
							display: grid;
							grid-template-columns: auto 1fr;
							gap: 8px;
							& > .name { grid-column: 1; opacity: .5; line-height: calc(1.5em + 2 * 8px + 2 * 1px); }
							&.dark { background: #2e2a32; color: #fff; }
							.category {
								display: flex;
								flex-flow: row wrap;
								gap: 8px;
								> * { flex: 0 0 auto; max-width: 100%; }
							}
						}
						.dropdown-header { padding: 8px 12px; font-weight: bold; }
					}
				`}
				</style>
				<div class="customization" onInputCapture={this.updateCustomButton}>
					<strong>Custom button:</strong>
					<label>content <input type="text" name="content" value={custom.content} /></label>
					<div class="group">
						<label><input type="checkbox" name="text" checked={custom.text} /> text</label>
						<label><input type="checkbox" name="outline" checked={custom.outline} /> outline</label>
						<label><input type="checkbox" name="primary" checked={custom.primary} /> primary</label>
						<label><input type="checkbox" name="danger" checked={custom.danger} /> danger</label>
						<label><input type="checkbox" name="small" checked={custom.small} /> small</label>
						<label><input type="checkbox" name="expandOnHover" checked={custom.expandOnHover} /> expand on hover</label>
					</div>
					<div class="group">
						<label>
							icon
							<select name="icon">
								{Object.keys(icons).map(icon => <option value={icon} selected={custom.icon === icon}>{icon}</option>)}
							</select>
						</label>
						<label>
							alt icon
							<select name="iconAlt">
								{Object.keys(icons).map(icon => <option value={icon} selected={custom.iconAlt === icon}>{icon}</option>)}
							</select>
						</label>
						<label>
							icon size
							<select name="iconSize">
								<option value="small" selected={custom.iconSize === 'small'}>small</option>
								<option value="medium" selected={custom.iconSize === 'medium'}>medium</option>
								<option value="large" selected={custom.iconSize === 'large'}>large</option>
							</select>
						</label>
					</div>
					<div class="group">
						<label><input type="checkbox" name="disabled" checked={custom.disabled} /> disabled</label>
						<label><input type="checkbox" name="loading" checked={custom.loading} /> loading</label>
						<label>
							<input type="checkbox" name="counterEnabled" checked={custom.counterEnabled} /> counter
							{/* <input type="range" name="counter" min="0" max="10" step="1" value={custom.counter} /> */}
							<input type="text" name="counter" min="0" step="1" value={custom.counter} />
						</label>
					</div>
					<label>
						dropdown actions
						<select name="dropdown">
							<option value="" selected={!custom.dropdown}>none</option>
							<option value="extra" selected={custom.dropdown === 'extra'}>extra</option>
							<option value="self" selected={custom.dropdown === 'self'}>self</option>
						</select>
					</label>
				</div>
				{this.buttons(false)}
				{this.buttons(true)}
			</div>
		);
	}
}

let toastCount = 0;
class ToastTest extends Component {
	showToast(e) {
		const iconKeys = Object.keys(icons);
		const data = e.target?.dataset;
		toast(`Toast! ${++toastCount}`, {
			icon: data?.icon || iconKeys[Math.floor(Math.random() * iconKeys.length)],
			stubborn: data?.stubborn,
			duration: data?.duration,
		});
	}
	render() {
		return (
			<div class="toast-test" style="padding: 16px; display: flex; flex-flow: row wrap; gap: 8px;">
				<Button outline onClick={this.showToast}>Show (light dismissable)</Button>
				<Button outline onClick={this.showToast} data-stubborn>Show (stubborn)</Button>
				<Button outline onClick={this.showToast} data-stubborn data-duration="3000">Show (stubborn + timed)</Button>
				<Button outline onClick={this.showToast} data-icon="spinner">Show (spinner)</Button>
			</div>
		);
	}
}

class SortTest extends Component {
	constructor(props) {
		super(props);

		this.state = {
			items: [
				'a',
				'b',
				'c',
				'd\ne',
				['x', 'y', 'z'],
				'f',
			],
		};

		this.update = this.update.bind(this);
	}

	update(e) {
		let parent = this.state.items;
		const path = e.target?.dataset.path;
		if (path) {
			parent = path.split('.').slice(0, -1).reduce((o, p) => o[p], parent);
		}
		parent.splice(e.to, 0, parent.splice(e.from, 1)[0]);
		this.setState({ items: this.state.items });
	}

	renderList(items, path = '') {
		return (
			<Sortable class="sortable" onChange={this.update} data-path={path}>
				{items.map((item, i) => <SortableItem><Handle class="handle" />{Array.isArray(item) ? this.renderList(item, `${path}${i}.`) : item}</SortableItem>)}
			</Sortable>
		);
	}


	render() {
		return (
			<div class="sortable-test">
				<style>{`
					.sortable-test {
						padding: 16px;
						white-space: pre-wrap;
						.sortable { border: 1px solid #ccc; border-radius: 6px; padding: 8px; }
						.sortable > div { display: flex; align-items: start; gap: .25em; }
						.handle { height: 1lh; }
						output::before { content: 'Current data:'; margin-right: .5em; opacity: .5; }
					}
				`}
				</style>
				{this.renderList(this.state.items)}
				<output>
					{JSON.stringify(this.state.items).replaceAll(',', ', ').replaceAll('\\n', ' ')}
				</output>
			</div>
		);
	}
}

class HelpTooltipTest extends Component {
	constructor(props) {
		super(props);

		this.state = {
			always: false,
			content: 'Tooltip text here',
			direction: 'right',
			align: 'center',
			abspos: false,
			tipLength: 8,
			tipWidth: 12,
			spacing: 4,
			tipAlignOffset: 6,
		};

		this.updateSettings = this.updateSettings.bind(this);
	}

	updateSettings(e) {
		const { name, value, type, checked } = e.target;
		this.setState({ [name]: type === 'checkbox' ? checked : value });
	}

	renderSide(dark) {
		const state = this.state;
		return (
			<div class={`side${dark ? ' dark' : ''}`}>
				<HelpTooltip
					always={state.always}
					direction={state.direction}
					align={state.align}
					style={state.css ? `--tip-length:${state.tipLength}px;--tip-width:${state.tipWidth}px;--spacing:${state.spacing}px;--tip-align-offset:${state.tipAlignOffset}px;` : null}
					dark={dark}
				>
					{state.content}
				</HelpTooltip>
			</div>
		);
	}

	render(props, state) {
		return (
			<div class="helptooltip-test">
				<style>{`
					.helptooltip-test {
						display: grid;
						grid-template-columns: 1fr 1fr;
						overflow: hidden;
						.customization {
							grid-column: 1 / -1;
							border-bottom: 1px solid #eee;
							padding: 16px;
							display: flex;
							flex-flow: column;
							align-items: start;
							gap: 8px;
							accent-color: var(--primary);
							.group { display: flex; gap: 16px; }
							label { display: flex; align-items: center; gap: 4px; }
							textarea, input[type="text"], input[type="number"] { line-height: 1; }
							input[type="number"] { width: 3em; }
							textarea, select, input { border: 1px solid #ccc; border-radius: 4px; background: #fff; }
						}
						.side {
							padding: 16px;
							background: #fff;
							&.dark { background: #2e2a32; color: #fff; }
						}
						.mover { display: contents; }
						.placer { display: none; }
						.mover.abs {
							display: flex;
							flex-flow: column;
							align-items: end;
							position: fixed;
							top: 0; left: 0;
							z-index: 99;
							pointer-events: none;
							> * { pointer-events: auto; }
							.placer {
								display: block;
								width: 16px; height: 16px;
								max-width: calc(100vw - 16px); max-height: calc(100vh - 16px);
								resize: both; overflow: hidden;
								background: #000;
								mask: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="20" height="20"%3E%3Ccircle cx="15" cy="11" r="3" /%3E%3Ccircle cx="25" cy="11" r="3" /%3E%3Ccircle cx="15" cy="20" r="3" /%3E%3Ccircle cx="25" cy="20" r="3" /%3E%3Ccircle cx="15" cy="29" r="3" /%3E%3Ccircle cx="25" cy="29" r="3" /%3E%3C/svg%3E') right bottom / 16px 16px no-repeat;
								clip-path: inset(calc(100% - 16px) 0 0 calc(100% - 16px));
							}
							.side { border: 1px solid #2e2a32; }
						}
						&:has([name="direction"] :is([value="top"], [value="bottom"]):checked) .mover.abs {
							flex-flow: row;
							/* .side { min-width: 150px; } */
						}
					}
					/* hacky fix */
					main:has(.helptooltip-test .mover.abs) { filter: none; clip-path: none; }
				`}
				</style>
				<div class="customization" onInputCapture={this.updateSettings}>
					<div class="group">
						<label><input type="checkbox" name="always" checked={state.always} /> always on</label>
					</div>
					<div class="group">
						<label>content <textarea name="content" rows="1">{state.content}</textarea></label>
					</div>
					<div class="group">
						<label>
							direction
							<select name="direction">
								<option value="right" selected={state.direction === 'right'}>right</option>
								<option value="left" selected={state.direction === 'left'}>left</option>
								<option value="top" selected={state.direction === 'top'}>top</option>
								<option value="bottom" selected={state.direction === 'bottom'}>bottom</option>
							</select>
						</label>
						<label>
							align
							<select name="align">
								<option value="start" selected={state.align === 'start'}>start</option>
								<option value="center" selected={state.align === 'center'}>center</option>
								<option value="end" selected={state.align === 'end'}>end</option>
							</select>
						</label>
					</div>
					<div class="group">
						<label><input type="checkbox" name="css" checked={state.css} /> custom CSS &rarr; </label>
						<label><input type="number" name="tipLength" value={state.tipLength} min="0" /> tip-length</label>
						<label><input type="number" name="tipWidth" value={state.tipWidth} min="0" /> tip-width</label>
						<label><input type="number" name="spacing" value={state.spacing} /> spacing</label>
						<label><input type="number" name="tipAlignOffset" value={state.tipAlignOffset} /> tip-align-offset</label>
					</div>
					<div class="group">
						<label><input type="checkbox" name="abspos" checked={state.abspos} /> test screen edges (auto-flip based on available space)</label>
					</div>
				</div>
				<div class={`mover${state.abspos ? ' abs' : ''}`}>
					<div class="placer" />
					{this.renderSide(false)}
					{this.renderSide(true)}
				</div>
			</div>
		);
	}
}


// const FieldLightDark = ({ children: [child] }) => (
// 	<div class="content">
// 		<div class="light">{child}</div>
// 		<div class="dark">{{ ...child, attributes: { ...child?.attributes, dark: true } }}</div>
// 	</div>
// );
class FieldsTest extends Component {
	constructor(props) {
		super(props);

		this.state = {
			richInline: false,
			richFloating: false,
			controlledValues: {},
			customSimple: {
				label: 'Custom Field',
				help: 'Help text here',
				placeholder: 'Enter value...',
				preText: '',
				icon: 'none',
				type: 'text',
				maxLength: '',
			},
		};

		this.updateSettings = this.updateSettings.bind(this);
		this.updateCustomSimple = this.updateCustomSimple.bind(this);
		this.controlled = this.controlled.bind(this);
	}

	updateSettings(e) {
		const { name, value, type, checked } = e.target;
		this.setState({ [name]: type === 'checkbox' ? checked : value });
	}

	updateCustomSimple(e) {
		const { name, value, type, checked } = e.target;
		const customSimple = {
			...this.state.customSimple,
			[name]: type === 'checkbox' ? checked : value,
		};
		this.setState({ customSimple });
	}

	controlled(e) {
		const input = e.currentTarget;
		if (!input?.name) return;
		const controlledValues = this.state.controlledValues;
		controlledValues[input.name] = input.value;
		this.setState({ controlledValues });
	}

	renderCustomSimpleField(dark) {
		const c = this.state.customSimple;
		return (
			<SimpleField
				dark={dark}
				label={c.label}
				help={c.help}
				placeholder={c.placeholder}
				preText={c.preText || undefined}
				icon={c.icon !== 'none' ? c.icon : undefined}
				type={c.type}
				maxLength={c.maxLength ? parseInt(c.maxLength, 10) : undefined}
				multiline={c.multiline}
				toggleable={c.toggleable}
				showOnHover={c.showOnHover}
				readonly={c.readonly}
				disabled={c.disabled}
				copyable={c.copyable}
				gray={c.gray}
				value={c.copyable ? 'Value to copy' : undefined}
				key="custom"
			/>
		);
	}

	renderSimpleFields(dark) {
		return [
			<SimpleField dark={dark} label="Main" help="Help here" key="simple" />,
			<SimpleField dark={dark} label="Password + Toggleable (hide disabled)" type="password" toggleable hideDisabled autocomplete="new-password" key="password" />,
			<SimpleField dark={dark} label="Read-only + Error" readonly value="Value" error="Error message" key="readonly" />,
			<SimpleField
				dark={dark}
				label="Multiline + Toggleable + Default Value (controlled)"
				multiline
				toggleable
				name={dark ? 'multilineDark' : 'multilineLight'}
				value={this.state.controlledValues[dark ? 'multilineDark' : 'multilineLight']}
				onInput={this.controlled}
				defaultValue="default"
				key="multiline"
			/>,
			<SimpleField dark={dark} label="With keywords (InputWithKeywords)" help="Type '[' to show suggestions" key="rich-input">
				<datalist type="keywords">
					<option value="MY_KEYWORD">Keyword</option>
					<option value="RELEASE">Release Date (<code>01/01/2027</code>)</option>
					<option value="OTHER_WITH_NO_LABEL" />
				</datalist>
			</SimpleField>,
		];
	}
	renderDateFields(dark) {
		return [
			<DateField dark={dark} label="Date" help="Help here" key="date" />,
			<DateField dark={dark} label="Allow custom precision + Toggleable" allowCustomPrecision toggleable key="toggle" />,
		];
	}
	renderLinkFields(dark) {
		return [
			<LinkField
				dark={dark}
				label="Link"
				help="Help here"
				options={[{ name: 'Page 1', destination: '/' }, { name: 'Page 2', destination: '/about/' }]}
				key="link"
			/>,
		];
	}

	renderDropdownFields(dark) {
		return [
			<DropdownField dark={dark} label="Simple" help="Help here" placeholder="Select a value..." key="main">
				<option value="1">Option 1</option>
				<option value="2">Option 2</option>
				<option value="3">Option 3</option>
			</DropdownField>,
			<DropdownField dark={dark} label="Searchable" searchable searchPlaceholder="Search values..." initialValue="option-1" key="searchable">
				{Array.from({ length: 100 }, (_, i) => <option value={`option-${i + 1}`}><code>#{`${i + 1}`.padStart(3, '0')}</code>: {(1e3 + 511 * i).toString(36)}</option>)}
			</DropdownField>,
		];
	}

	renderAutocompleteFields(dark) {
		return [
			<AutocompleteField dark={dark} label="Basic" help="Type to filter options" placeholder="Search..." key="basic">
				<option value="apple">Apple</option>
				<option value="banana">Banana</option>
				<option value="cherry">Cherry</option>
				<option value="date">Date</option>
				<option value="elderberry">Elderberry</option>
			</AutocompleteField>,
			<AutocompleteField dark={dark} label="With custom option" placeholder="Type to search or create..." key="custom">
				<option value="red">Red</option>
				<option value="green">Green</option>
				<option value="blue">Blue</option>
				<option data-custom>Create "%s"</option>
			</AutocompleteField>,
			<AutocompleteField dark={dark} label="With preText" preText="@" placeholder="username" key="pretext">
				<option value="alice">alice</option>
				<option value="bob">bob</option>
				<option value="charlie">charlie</option>
			</AutocompleteField>,
		];
	}

	renderCardSelectFields(dark) {
		const name = `card-select-${dark ? 'dark' : 'light'}`;
		return [
			<CardSelectField
				dark={dark}
				name={name}
				label="Text only"
				help="Help here"
				value={this.state[name] || 'a'}
				onInput={this.updateSettings}
				key="text"
			>
				<option value="a" label="Option A" description="A short description of what this option does." />
				<option value="b" label="Option B" subtext="(with subtext)" description="Another description, slightly longer than the first one." />
				<option value="c" label="Option C" description="A third option." />
			</CardSelectField>,
			<CardSelectField
				dark={dark}
				name={`${name}-image`}
				label="With images"
				value={this.state[`${name}-image`] || 'link'}
				onInput={this.updateSettings}
				key="image"
			>
				<option value="link" label="Icon" image={getIcon('share')} description="Pass a VNode to the image attribute." />
				<option value="image" label="Image" subtext="(with subtext)" image="https://s3-eu-west-1.amazonaws.com/st-static-stage/universalstudios/universal-cms/images/heros/universal-hero-default.png" description="Or pass a URL string to render an image." />
			</CardSelectField>,
			<CardSelectField dark={dark} name={`${name}-disabled`} label="Disabled" value="a" disabled key="disabled">
				<option value="a" label="Option A" description="Cannot be changed." />
				<option value="b" label="Option B" description="Cannot be selected." />
			</CardSelectField>,
		];
	}

	renderRichFields(dark) {
		const { richInline, richFloating } = this.state;
		const name = `rich-value-${dark ? 'dark' : 'light'}`;
		return [
			<RichTextField
				name={name}
				defaultValue={this.state[name] || ''}
				onInput={this.updateSettings}
				label="Name"
				help="Help here"
				inline={richInline}
				floating={richFloating}
				// these options cannot be changed dynamically - if changed the component must be remounted (which happens if the key doesn't match)
				key={`${!!richInline}-${!!richFloating}`}
			/>,
		];
	}

	render(props, state) {
		return (
			<div class="rich-text-test">
				<style>{`
					.rich-text-test {
						padding: 8px 16px 16px;
						summary { opacity: .5; cursor: default; }
						.content + .name { margin-top: 16px; }
						.settings {
							display: flex;
							flex-flow: row wrap;
							align-items: center;
							gap: 4px 8px;
							label { display: flex; align-items: center; gap: 4px; }
						}
						.content {
							display: grid;
							grid-template-columns: 1fr 1fr;
							grid-template-rows: auto;
							margin: 8px 0;
							.light, .dark {
								display: grid;
								gap: 8px;
								padding: 8px;
							}
							.light { box-shadow: inset 0 0 0 1px #eee; border-radius: 6px 0 0 6px; }
							.dark { background: #2e2a32; color: #fff; border-radius: 0 6px 6px 0; }
						}
					}
				`}
				</style>
				<details>
					<summary>Simple</summary>
					<strong>Custom SimpleField:</strong>
					<div class="settings" onInputCapture={this.updateCustomSimple}>
						<label>label <input type="text" name="label" value={state.customSimple.label} /></label>
						<label>help <input type="text" name="help" value={state.customSimple.help} /></label>
						<label>placeholder <input type="text" name="placeholder" value={state.customSimple.placeholder} /></label>
						<label>preText <input type="text" name="preText" value={state.customSimple.preText} /></label>
						<label>
							icon
							<select name="icon">
								{Object.keys(icons).map(icon => <option value={icon} selected={state.customSimple.icon === icon}>{icon}</option>)}
							</select>
						</label>
						<label>
							type
							<select name="type">
								<option value="text" selected={state.customSimple.type === 'text'}>text</option>
								<option value="password" selected={state.customSimple.type === 'password'}>password</option>
								<option value="email" selected={state.customSimple.type === 'email'}>email</option>
								<option value="number" selected={state.customSimple.type === 'number'}>number</option>
								<option value="url" selected={state.customSimple.type === 'url'}>url</option>
							</select>
						</label>
						<label>maxLength <input type="number" name="maxLength" value={state.customSimple.maxLength} style="width: 4em" min="0" /></label>
					</div>
					<div class="settings" onInputCapture={this.updateCustomSimple}>
						<label><input type="checkbox" name="multiline" checked={state.customSimple.multiline} /> multiline</label>
						<label><input type="checkbox" name="toggleable" checked={state.customSimple.toggleable} /> toggleable</label>
						<label><input type="checkbox" name="showOnHover" checked={state.customSimple.showOnHover} /> showOnHover</label>
						<label><input type="checkbox" name="readonly" checked={state.customSimple.readonly} /> readonly</label>
						<label><input type="checkbox" name="disabled" checked={state.customSimple.disabled} /> disabled</label>
						<label><input type="checkbox" name="copyable" checked={state.customSimple.copyable} /> copyable</label>
						<label><input type="checkbox" name="gray" checked={state.customSimple.gray} /> gray</label>
					</div>
					<div class="content">
						<div class="light">
							{this.renderCustomSimpleField(false)}
							{this.renderSimpleFields(false)}
						</div>
						<div class="dark">
							{this.renderCustomSimpleField(true)}
							{this.renderSimpleFields(true)}
						</div>
					</div>
				</details>
				<details>
					<summary>Date</summary>
					<div class="content">
						<div class="light">{this.renderDateFields(false)}</div>
						<div class="dark">{this.renderDateFields(true)}</div>
					</div>
				</details>
				<details>
					<summary>Link</summary>
					<div class="content">
						<div class="light">{this.renderLinkFields(false)}</div>
						<div class="dark">{this.renderLinkFields(true)}</div>
					</div>
				</details>
				<details>
					<summary>Dropdown</summary>
					<div class="content">
						<div class="light">{this.renderDropdownFields(false)}</div>
						<div class="dark">{this.renderDropdownFields(true)}</div>
					</div>
				</details>
				<details>
					<summary>Autocomplete</summary>
					<div class="content">
						<div class="light">{this.renderAutocompleteFields(false)}</div>
						<div class="dark">{this.renderAutocompleteFields(true)}</div>
					</div>
				</details>
				<details>
					<summary>CardSelect</summary>
					<div class="content">
						<div class="light">{this.renderCardSelectFields(false)}</div>
						<div class="dark">{this.renderCardSelectFields(true)}</div>
					</div>
				</details>
				<details>
					<summary>RichText</summary>
					<div class="settings" onInputCapture={this.updateSettings}>
						<label><input type="checkbox" name="richInline" checked={state.richInline} /> Inline (single-line text)</label>
						<label><input type="checkbox" name="richFloating" checked={state.richFloating} /> Floating (tooltip toolbar on selection)</label>
					</div>
					<div class="content">
						<div class="light">{this.renderRichFields(false)}</div>
						<div class="dark">{this.renderRichFields(true)}</div>
					</div>
				</details>
			</div>
		);
	}
}

class MiscComponentsTest extends Component {
	constructor(props) {
		super(props);

		this.state = {
			warningText: 'You have unsaved changes',
			warningSubtext: 'These changes will be included in the preview link',
		};

		this.updateSettings = this.updateSettings.bind(this);
	}

	updateSettings(e) {
		const { name, value, type, checked } = e.target;
		this.setState({ [name]: type === 'checkbox' ? checked : value });
	}

	renderSide(dark) {
		const { warningText, warningSubtext } = this.state;
		return (
			<div class={`side${dark ? ' dark' : ''}`}>
				<div class="section">
					<div class="name">Badge</div>
					<div class="items">
						<Badge dark={dark} type="live" />
						<Badge dark={dark} type="draft" />
						<Badge dark={dark} type="live" alert />
						<Badge dark={dark} type="draft" alert />
					</div>
				</div>
				<div class="section">
					<div class="name">Warning</div>
					<div class="items">
						<Warning>
							<div>{warningText}</div>
							{warningSubtext && <div>{warningSubtext}</div>}
						</Warning>
					</div>
				</div>
			</div>
		);
	}

	render(props, state) {
		return (
			<div class="misc-components-test">
				<style>{`
					.misc-components-test {
						.customization {
							padding: 16px;
							border-bottom: 1px solid #eee;
							display: flex;
							flex-flow: row wrap;
							align-items: center;
							gap: 8px 16px;
							accent-color: var(--primary);
							label { display: flex; align-items: center; gap: 4px; }
							select, input { border: 1px solid #ccc; border-radius: 4px; background: #fff; }
						}
						.sides {
							display: grid;
							grid-template-columns: 1fr 1fr;
						}
						.side {
							padding: 16px;
							&.dark { background: #2e2a32; color: #fff; }
						}
						.section {
							& + .section { margin-top: 16px; }
							.name { opacity: .5; margin-bottom: 8px; }
							.items { display: flex; flex-flow: row wrap; gap: 8px; align-items: center; }
						}
					}
				`}
				</style>
				<div class="customization" onInputCapture={this.updateSettings}>
					<strong>Warning:</strong>
					<label>text <input type="text" name="warningText" value={state.warningText} style="width: 200px" /></label>
					<label>subtext <input type="text" name="warningSubtext" value={state.warningSubtext} style="width: 300px" /></label>
				</div>
				<div class="sides">
					{this.renderSide(false)}
					{this.renderSide(true)}
				</div>
			</div>
		);
	}
}

class IconsTest extends Component {
	constructor(props) {
		super(props);

		this.state = {
			color: null,
			size: '16px',
		};

		this.updateColor = this.updateColor.bind(this);
		this.revertColor = this.revertColor.bind(this);
		this.updateSize = this.updateSize.bind(this);
	}

	updateColor(e) {
		this.setState({ color: e.target.value });
	}
	revertColor() {
		this.setState({ color: null });
	}
	updateSize(e) {
		this.setState({ size: e.target.value });
	}

	renderIcons() {
		return Object.keys(icons).map(name => <div class="icon">{getIcon(name)}<span>{name}</span></div>);
	}

	render() {
		let style = '';
		const color = this.state.color;
		if (color) style += `--color:${color};`;
		const size = this.state.size;
		if (size) style += `--size:${size};`;
		return (
			<div class="icons-wrapper" style={style}>
				<legend>
					<code>Icons</code>
					<select name="size" value={size} onChange={this.updateSize}>
						<option value="16px">S</option>
						<option value="24px">M</option>
						<option value="32px">L</option>
					</select>
					<label class="color">
						<input type="color" onInput={this.updateColor} value={color || '#000000'} />
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"><path d="M480-96q-79 0-149-30t-122-82-83-123-30-149q0-80 31-149t84-122 125-83 152-30q78 0 147 27t119 74 81 110 29 135q0 96-67 163t-163 67h-68q-8 0-14 5t-6 13q0 15 15 25t15 53q0 37-27 67t-69 29M264-444q25 0 43-17t17-43-17-42-43-18-42 18-18 42 18 43 42 17m120-144q25 0 43-17t17-43-17-42-43-18-42 18-18 42 18 43 42 17m192 0q25 0 43-17t17-43-17-42-43-18-42 18-18 42 18 43 42 17m120 144q25 0 43-17t17-43-17-42-43-18-42 18-18 42 18 43 42 17M480-168q11 0 18-8t6-16q0-16-15-28t-15-50 27-64 64-26h69q66 0 112-46t46-112q0-115-88-194t-216-80q-134 0-227 91t-93 221 91 221 221 91" /></svg>
					</label>
					<button onClick={this.revertColor}>{getIcon('revert')}</button>
				</legend>
				<div class="icons-test">
					<style>{`
						.icons-wrapper {
							display: contents;
							legend {
								display: flex;
								align-items: center;
								> label, > button, > select {
									position: relative;
									display: inline-block;
									width: 26px;
									height: 26px;
									border: 1px solid #ccc;
									border-radius: 50%;
									background: #fff;
									color: #000;
									overflow: hidden;
									cursor: pointer;
								}
								svg {
									position: absolute;
									top: 50%; left: 50%;
									width: 16px; height: 16px;
									translate: -50% -50%;
								}
								&:is(:hover, :has(:focus-visible)) .color + button { --translate-amount: 22px; }
								select {
									margin-left: 8px;
									appearance: none;
									text-align: center;
									font-weight: bold;
									option[value="16px"] { font-size: 10px; }
									option[value="24px"] { font-size: 14px; }
									option[value="32px"] { font-size: 18px; }
								}
							}
							.color {
								margin-left: 8px;
								background: var(--color, #fff);
								--l: clamp(0, (l / 0.623 - 1) * -infinity, 1);
								color: oklch(from var(--color, #fff) var(--l) 0 h);
								z-index: 1;
								&:has(:focus-visible) { outline: 2px auto; }
								input { position: absolute; inset: 0; width: auto; height: auto; opacity: 0; cursor: pointer; }
								+ button {
									margin-left: -26px;
									--translate-amount: 10px;
									translate: if(style(--color: initial): 0; else: var(--translate-amount)) 0;
									transition: translate .15s;
									svg { width: 16px; height: 16px; }
								}
							}
						}
						.icons-test {
							display: flex;
							.light, .dark {
								position: relative;
								flex: 1 1 auto;
								display: grid;
								grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
								gap: 4px;
								padding: 20px 16px 8px;
								margin-top: -11px;
								--bg: #fff;
								--default-color: currentColor;
								background: var(--bg);
								color: var(--color, var(--default-color));
								border-radius: 8px 0 0 8px;
							}
							.dark { --bg: #2e2a32; --default-color: #fff; border-radius: 0 8px 8px 0; }
							.icon {
								display: flex;
								gap: 8px;
								align-items: center;
								cursor: default;
								svg {
									flex: 0 0 auto;
									height: var(--size, 24px);
									width: auto;
									background: var(--bg);
									--checker-strength: 0%;
									--alt: color-mix(in srgb, currentColor var(--checker-strength), var(--bg));
									background: conic-gradient(var(--bg) 25%, var(--alt) 25% 50%, var(--bg) 50% 75%, var(--alt) 75%) 0 0 / 8px 8px;
									border: 1px solid transparent;
									box-sizing: content-box;
									border-radius: 4px;
									z-index: 0;
									pointer-events: none;
									transition: scale .15s, border-color .15s, --checker-strength .15s;
									animation: icons-z-index .15s;
								}
								span { transition: translate .15s; user-select: all; }
								&:hover svg {
									--checker-strength: 5%;
									/* scale: 3; */
									scale: tan(atan2(72px, var(--size, 24px)));
									z-index: 2;
									border-color: color-mix(in oklch, currentColor 10%, var(--bg));
									animation: none;
									~ span { translate: calc((72px - var(--size, 24px)) / 2) 0; }
								}
							}
						}
						@keyframes icons-z-index { from { z-index: 1; } }
						@property --checker-strength { syntax: '<percentage>'; inherits: false; initial-value: 0%; }
					`}
					</style>
					<div class="light">{this.renderIcons()}</div>
					<div class="dark">{this.renderIcons()}</div>
				</div>
			</div>
		);
	}
}